<template>
  <li
    class="app-menu-desktop__nav-item"
    @mouseover="open = true"
    @mouseleave="open = false"
  >
    <NuxtLink
      :to="item.url"
      :external="item.external"
      :target="item.external ? '_blank' : undefined"
      :rel="item.external ? 'noopener noreferrer' : undefined"
      class="text-button"
    >
      {{ item.title }}
    </NuxtLink>

    <Transition name="fade" @after-leave="onDropdownClosed">
      <div
        v-show="item.children && open"
        ref="dropdown"
        class="app-menu-desktop__nav-item__dropdown"
      >
        <AppMenuDesktopNavMenu
          v-if="item.children"
          :menu="item.children"
          :active-id="levelOne?.id"
          :all-link="{ title: item.title, url: item.url, external: item.external }"
          style="z-index: 3"
          @set-level="setLevelOne"
        />

        <Transition name="desktop-nav-width">
          <AppMenuDesktopNavMenu
            v-if="levelOne?.children"
            :menu="levelOne.children"
            :active-id="levelTwo?.id"
            :all-link="{ title: levelOne.title, url: levelOne.url, external: levelOne.external }"
            style="z-index: 2"
            @set-level="setLevelTwo"
          />
        </Transition>

        <Transition name="desktop-nav-width">
          <AppMenuDesktopNavMenu
            v-if="levelTwo?.children"
            :menu="levelTwo.children"
            :all-link="{ title: levelTwo.title, url: levelTwo.url, external: levelTwo.external }"
            style="z-index: 1"
          />
        </Transition>
      </div>
    </Transition>
  </li>
</template>

<script setup lang="ts">
import type { MenuItem } from './AppMenuDesktopNav.vue'

type Props = {
  item: MenuItem
}

const props = defineProps<Props>()

const open = ref(false)

const levelOne = ref<MenuItem | undefined>(
  props.item.children?.find((child) => child.children),
)

const levelTwo = ref<MenuItem | undefined>()

const setLevelOne = (item?: MenuItem) => {
  levelOne.value = item
  levelTwo.value = undefined
}

const setLevelTwo = (item?: MenuItem) => {
  levelTwo.value = item
}

const onDropdownClosed = () => {
  levelOne.value = props.item.children?.find((child) => child.children)
  levelTwo.value = undefined
}

onBeforeRouteUpdate(() => {
  open.value = false
})
</script>

<style lang="scss">
@import '@boats/ui/styles/config';

.app-menu-desktop__nav-item {
  position: relative;
  margin-right: 1.25rem;

  &:last-child {
    margin-right: 0;
  }

  & > a {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 3.75rem;
    white-space: nowrap;
    text-decoration: none !important;

    &::before {
      content: '';
      position: absolute;
      top: 100%;
      left: 0;
      width: 100%;
      height: 0.25rem;
      margin-top: 0.375rem;
      background-color: currentColor;
      opacity: 0;
      pointer-events: none;
      @include transition('opacity');
    }

    &::after {
      content: '';
      position: absolute;
      bottom: -0.625rem;
      left: -0.75rem;
      right: -0.75rem;
      height: 100%;
      display: none;
    }
  }

  &:hover {
    & > a {
      &::before {
        opacity: 1;
      }

      &::after {
        display: block;
      }
    }
  }

  @include media-breakpoint-up(xxl) {
    margin-right: 2.5rem;

    & > a {
      &::after {
        left: -1.25rem;
        right: -1.25rem;
      }
    }
  }
}

.app-menu-desktop__nav-item__dropdown {
  position: absolute;
  top: 100%;
  left: -1.5rem;
  display: flex;
  margin-top: 0.625rem;
  padding: 2.5rem 0;
  color: $black;
  background-color: $white;
  box-shadow: 0.125rem 0.625rem 1.25rem 0 rgba(0, 0, 0, 0.08);
  border-radius: 0 0 0.5rem 0.5rem;
  z-index: 1;
}
</style>
